import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { addDoc, collection, doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { getDownloadURL, getStorage, ref, uploadBytesResumable } from 'firebase/storage';
import { appId, db } from '../../../firebase-config';
import { resolveScanValue } from '../../../core/identification/scanResolver';
import { resolveScanEventsCollectionPath, resolveStockProductDocPath } from '../../../core/company/companyPaths';
import {
  Icon,
  getDestinationOperationRoleLabel
} from './LogisticsUtils';

const STATUS_STYLES = {
  pending: {
    label: 'Pendiente',
    badge: 'bg-slate-700/70 text-slate-100 ring-1 ring-white/10'
  },
  en_camino: {
    label: 'En camino',
    badge: 'bg-cyan-500/20 text-cyan-100 ring-1 ring-cyan-300/30'
  },
  incomplete: {
    label: 'Incompleto',
    badge: 'bg-amber-500/20 text-amber-100 ring-1 ring-amber-300/30'
  },
  completed: {
    label: 'Completado',
    badge: 'bg-emerald-500/20 text-emerald-100 ring-1 ring-emerald-300/30'
  },
  cancelled: {
    label: 'Cancelado',
    badge: 'bg-rose-500/20 text-rose-100 ring-1 ring-rose-300/30'
  }
};

const getStatusMeta = (status) => STATUS_STYLES[status] || STATUS_STYLES.pending;

const buildOperationLabel = (destination) => {
  const hasDeliveries = Number(destination?.deliveries) > 0;
  const hasPickups = Number(destination?.pickups) > 0;

  if (hasDeliveries && hasPickups) return 'Ent./Ret.';
  if (hasDeliveries) return 'Entrega';
  if (hasPickups) return 'Retiro';
  return 'Parada';
};

const buildQuantityLabel = (destination) => {
  const parts = [];
  if (Number(destination?.deliveries) > 0) parts.push(`Ent. ${destination.deliveries}`);
  if (Number(destination?.pickups) > 0) parts.push(`Ret. ${destination.pickups}`);
  return parts.join(' · ');
};

const buildRouteActorSnapshot = (route = null) => {
  const driverName = [
    String(route?.driverName || '').trim(),
    String(route?.driverLastName || '').trim()
  ]
    .filter(Boolean)
    .join(' ')
    .trim();

  return {
    transportIdSnapshot:
      String(route?.transporterId || route?.transportId || route?.assignedTransportId || '').trim(),
    transportNameSnapshot:
      String(
        route?.transporterName ||
          route?.transportName ||
          route?.vehicleLabel ||
          route?.transport ||
          route?.transportType ||
          ''
      ).trim(),
    driverIdSnapshot: String(route?.driverId || route?.currentDriverId || '').trim(),
    driverNameSnapshot:
      driverName ||
      String(route?.currentDriverName || route?.driverNameSnapshot || route?.driverName || '').trim(),
    driverPhotoURLSnapshot:
      String(
        route?.driverPhotoURL ||
          route?.currentDriverPhotoURL ||
          route?.photoURL ||
          ''
      ).trim(),
  };
};

const formatNoteTime = (value) => {
  if (!value) return 'Ahora';
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleTimeString();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Ahora' : parsed.toLocaleTimeString();
};

const formatStockReasonLabel = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  const labels = {
    sale: 'Venta salida',
    warranty: 'Garantía salida',
    guarantee: 'Garantía salida',
    repair: 'Reparación salida',
    replacement: 'Reposición salida',
    transfer: 'Traslado salida',
    loan: 'Préstamo salida',
    return: 'Devolución salida',
    storage: 'Guardado',
  };
  return labels[normalized] || normalized.replace(/_/g, ' ');
};

const getScanTargetLabel = (targetType = '') => {
  const labels = {
    route: 'Ruta',
    destination: 'Destino',
    operational_load: 'Carga operativa',
    incoming_load: 'Carga recibida',
    product: 'Producto',
    multiple: 'Varias coincidencias',
    unknown: 'Sin coincidencia',
  };
  return labels[targetType] || 'Lectura';
};

const getScanResultTone = (status = '') => {
  if (status === 'found') return 'border-emerald-200 bg-emerald-50 text-emerald-800';
  if (status === 'multiple') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (status === 'not_found') return 'border-rose-200 bg-rose-50 text-rose-800';
  return 'border-slate-200 bg-slate-50 text-slate-700';
};

const buildLoadScanChips = (load = {}, linkedProduct = null) => {
  const chips = [];
  const pushChip = (label, value, tone) => {
    const cleanValue = String(value || '').trim();
    if (!cleanValue) return;
    if (chips.some((chip) => String(chip.value).trim().toLowerCase() === cleanValue.toLowerCase())) return;
    chips.push({ label, value: cleanValue, tone });
  };

  pushChip('Ref', load.referenceCode, 'slate');
  pushChip('Carga', load.id || load.loadId, 'slate');
  pushChip('SKU', load.stockSkuSnapshot || linkedProduct?.sku, 'slate');
  pushChip('Barra', load.stockBarcodeSnapshot || load.barcodeSnapshot || linkedProduct?.barcode, 'cyan');
  pushChip('QR', load.stockQrCodeSnapshot || load.qrCodeSnapshot || linkedProduct?.qrCode, 'emerald');
  pushChip('Producto', load.stockProductId || load.productId || linkedProduct?.id, 'indigo');

  return chips;
};

const resolveOperationalLoadLabel = (load = {}, linkedProduct = null) => {
  const rawTitle = String(load?.title || '').trim();
  const looksTechnicalId =
    /^purchase-load-/i.test(rawTitle) ||
    /^outbound-load-/i.test(rawTitle) ||
    /^load-/i.test(rawTitle);
  const looksTooShort = rawTitle.length > 0 && rawTitle.length <= 2;

  const productSnapshotLabel = String(
    load?.productNameSnapshot ||
      load?.stockProductNameSnapshot ||
      load?.productName ||
      linkedProduct?.name ||
      ''
  ).trim();

  if (productSnapshotLabel && (looksTechnicalId || looksTooShort)) {
    return productSnapshotLabel;
  }

  if (rawTitle && !looksTechnicalId) return rawTitle;

  return (
    productSnapshotLabel || 'Producto sin definir'
  );
};

const getScanChipToneClass = (tone = 'slate') => {
  const tones = {
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    slate: 'border-slate-200 bg-white text-slate-600',
  };
  return tones[tone] || tones.slate;
};

const DETAIL_HEADER_AUTO_COLLAPSE_MS = 3000;
const EVIDENCE_IMAGE_MAX_SIZE = 1600;
const EVIDENCE_IMAGE_QUALITY = 0.72;
const EVIDENCE_COMPRESSION_TIMEOUT_MS = 18000;
const EVIDENCE_UPLOAD_TIMEOUT_MS = 50000;
const EVIDENCE_SAVE_TIMEOUT_MS = 18000;

const buildEvidenceUploadState = (overrides = {}) => ({
  active: false,
  phase: 'idle',
  label: '',
  detail: '',
  progress: 0,
  ...overrides,
});

const sanitizeStorageFileName = (value = 'evidencia') => {
  const cleanValue = String(value || 'evidencia')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);

  return cleanValue || 'evidencia';
};

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);

  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };

  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('No se pudo leer la imagen.'));
  };

  image.src = objectUrl;
});

const withTimeout = (promise, timeoutMs, errorMessage) => new Promise((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    reject(new Error(errorMessage));
  }, timeoutMs);

  promise
    .then((value) => {
      clearTimeout(timeoutId);
      resolve(value);
    })
    .catch((error) => {
      clearTimeout(timeoutId);
      reject(error);
    });
});

const uploadEvidenceBlob = ({ storageRef, blob, metadata, onProgress }) => new Promise((resolve, reject) => {
  const uploadTask = uploadBytesResumable(storageRef, blob, metadata);
  const timeoutId = setTimeout(() => {
    uploadTask.cancel();
    reject(new Error('La subida tardó demasiado. Revisá si Storage está habilitado y volvé a intentar.'));
  }, EVIDENCE_UPLOAD_TIMEOUT_MS);

  uploadTask.on(
    'state_changed',
    (snapshot) => {
      const totalBytes = Number(snapshot.totalBytes) || 0;
      const transferredBytes = Number(snapshot.bytesTransferred) || 0;
      const progress = totalBytes > 0 ? Math.round((transferredBytes / totalBytes) * 100) : 0;
      onProgress?.(Math.min(100, Math.max(0, progress)));
    },
    (error) => {
      clearTimeout(timeoutId);
      reject(error);
    },
    () => {
      clearTimeout(timeoutId);
      resolve(uploadTask.snapshot);
    }
  );
});

const getEvidenceUploadErrorMessage = (error) => {
  const code = String(error?.code || '');
  const message = String(error?.message || '');

  if (code.includes('storage/unauthorized')) {
    return 'Storage rechazó la evidencia. Revisá reglas/permisos del proyecto G-CENTRA.';
  }

  if (code.includes('storage/canceled') || message.includes('tardó demasiado')) {
    return 'La evidencia tardó demasiado en subir. Revisá conexión o si Storage está habilitado y probá de nuevo.';
  }

  if (code.includes('storage/bucket-not-found') || code.includes('storage/object-not-found')) {
    return 'No encuentro el bucket de Storage configurado para G-CENTRA. Hay que habilitar Cloud Storage en Firebase.';
  }

  if (code.includes('storage/retry-limit-exceeded')) {
    return 'Storage no respondió a tiempo. La foto no se perdió: probá de nuevo con mejor conexión.';
  }

  return 'No pude subir la evidencia. Revisá Storage/permisos y volvé a intentar.';
};

const compressEvidenceImage = async (file) => {
  if (!file?.type?.startsWith('image/')) {
    return {
      blob: file,
      name: sanitizeStorageFileName(file?.name || 'evidencia'),
      contentType: file?.type || 'application/octet-stream',
      originalSize: file?.size || 0,
      compressedSize: file?.size || 0,
      compressionApplied: false,
      width: null,
      height: null,
    };
  }

  if (typeof document === 'undefined') {
    return {
      blob: file,
      name: sanitizeStorageFileName(file.name || 'evidencia.jpg'),
      contentType: file.type || 'image/jpeg',
      originalSize: file.size || 0,
      compressedSize: file.size || 0,
      compressionApplied: false,
      width: null,
      height: null,
    };
  }

  const image = await loadImageFromFile(file);
  const scale = Math.min(1, EVIDENCE_IMAGE_MAX_SIZE / Math.max(image.width, image.height, 1));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) {
    return {
      blob: file,
      name: sanitizeStorageFileName(file.name || 'evidencia.jpg'),
      contentType: file.type || 'image/jpeg',
      originalSize: file.size || 0,
      compressedSize: file.size || 0,
      compressionApplied: false,
      width: image.width,
      height: image.height,
    };
  }

  context.drawImage(image, 0, 0, width, height);

  const blob = await new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', EVIDENCE_IMAGE_QUALITY);
  });

  if (!blob) {
    return {
      blob: file,
      name: sanitizeStorageFileName(file.name || 'evidencia.jpg'),
      contentType: file.type || 'image/jpeg',
      originalSize: file.size || 0,
      compressedSize: file.size || 0,
      compressionApplied: false,
      width: image.width,
      height: image.height,
    };
  }

  const baseName = sanitizeStorageFileName((file.name || 'evidencia').replace(/\.[^.]+$/, ''));
  return {
    blob,
    name: `${baseName}.jpg`,
    contentType: 'image/jpeg',
    originalSize: file.size || 0,
    compressedSize: blob.size || 0,
    compressionApplied: true,
    width,
    height,
  };
};

const DestinationDetailModal = ({
  destination,
  onClose,
  onUpdate,
  showMessage,
  userRole,
  companyId = 'default',
  routeId = '',
  route = null
}) => {
  const [orderDetail, setOrderDetail] = useState(
    destination?.orderDetail || destination?.description || ''
  );
  const [adminNote, setAdminNote] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [evidenceUploadState, setEvidenceUploadState] = useState(buildEvidenceUploadState());
  const [evidenceSuccessMessage, setEvidenceSuccessMessage] = useState('');
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [actionHubOpen, setActionHubOpen] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [isSavingScan, setIsSavingScan] = useState(false);
  const [cameraScannerOpen, setCameraScannerOpen] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
  const [linkedStockProducts, setLinkedStockProducts] = useState([]);
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [showHeaderPulse, setShowHeaderPulse] = useState(false);
  const fileInputRef = useRef(null);
  const scanInputRef = useRef(null);
  const actionHubRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraScanTimerRef = useRef(null);
  const headerCollapseTimeoutRef = useRef(null);
  const actorSnapshot = useMemo(() => buildRouteActorSnapshot(route), [route]);

  const statusMeta = getStatusMeta(destination?.status);
  const operationLabel = buildOperationLabel(destination);
  const completionActionLabel =
    operationLabel === 'Entrega'
      ? 'Confirmar entrega'
      : operationLabel === 'Retiro'
        ? 'Confirmar retiro'
        : operationLabel === 'Ent./Ret.'
          ? 'Confirmar parada'
          : 'Marcar completado';
  const cancellationActionLabel =
    operationLabel === 'Entrega'
      ? 'Cancelar entrega'
      : operationLabel === 'Retiro'
        ? 'Cancelar retiro'
        : operationLabel === 'Ent./Ret.'
          ? 'Cancelar parada'
          : 'Marcar cancelado';
  const linkedStockProductsById = useMemo(
    () => new Map(linkedStockProducts.map((product) => [String(product.id), product])),
    [linkedStockProducts]
  );

  const locationLine = [
    destination?.locality,
    destination?.partido,
    destination?.province,
    destination?.postalCode
  ]
    .filter(Boolean)
    .join(', ');

  const metaItems = useMemo(() => {
    const items = [
      { icon: 'M3.75 12h16.5m-16.5 0l3.75-3.75M3.75 12l3.75 3.75', label: operationLabel }
    ];

    if (destination?.priority) {
      items.push({
        icon: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321 1.01l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.386a.562.562 0 01-.84.61L12 18.354l-4.978 3.208a.562.562 0 01-.84-.61l1.285-5.386a.563.563 0 00-.182-.557L3.08 10.407a.562.562 0 01.32-1.01l5.519-.442a.563.563 0 00.475-.345l2.125-5.11z',
        label: destination.priority === 'Normal' ? 'Normal' : destination.priority
      });
    }

    if (destination?.schedule) {
      items.push({
        icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
        label: destination.schedule
      });
    }

    const quantityLabel = buildQuantityLabel(destination);
    if (quantityLabel) {
      items.push({
        icon: 'M3 7.5l9-4.5 9 4.5M4.5 10.5l7.5 3.75m0 0l7.5-3.75M12 14.25V21',
        label: quantityLabel
      });
    }

    return items.slice(0, 4);
  }, [destination]);

  useEffect(() => {
    setIsHeaderCollapsed(false);
    setShowHeaderPulse(false);

    if (headerCollapseTimeoutRef.current) {
      clearTimeout(headerCollapseTimeoutRef.current);
    }

    headerCollapseTimeoutRef.current = setTimeout(() => {
      setIsHeaderCollapsed(true);
      setShowHeaderPulse(true);
    }, DETAIL_HEADER_AUTO_COLLAPSE_MS);

    return () => {
      if (headerCollapseTimeoutRef.current) {
        clearTimeout(headerCollapseTimeoutRef.current);
        headerCollapseTimeoutRef.current = null;
      }
    };
  }, [destination?.id]);

  const toggleHeaderCollapsed = () => {
    if (headerCollapseTimeoutRef.current) {
      clearTimeout(headerCollapseTimeoutRef.current);
      headerCollapseTimeoutRef.current = null;
    }

    setShowHeaderPulse(false);
    setIsHeaderCollapsed((value) => !value);
  };

  const openActionHub = () => {
    setActionHubOpen(true);
    window.setTimeout(() => {
      actionHubRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
  };

  const stopCameraScanner = () => {
    if (cameraScanTimerRef.current) {
      window.clearInterval(cameraScanTimerRef.current);
      cameraScanTimerRef.current = null;
    }

    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }

    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }

    setCameraScannerOpen(false);
    setIsCameraStarting(false);
  };

  useEffect(() => {
    return () => {
      if (cameraScanTimerRef.current) {
        window.clearInterval(cameraScanTimerRef.current);
        cameraScanTimerRef.current = null;
      }

      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const productIds = Array.from(
      new Set(
        [
          ...(destination?.operationalLoads || []),
          ...(destination?.incomingOperationalLoads || [])
        ]
          .map((load) => String(load?.stockProductId || load?.productId || '').trim())
          .filter(Boolean)
      )
    );

    if (!companyId || productIds.length === 0) {
      setLinkedStockProducts([]);
      return undefined;
    }

    let isCancelled = false;

    Promise.all(
      productIds.map(async (productId) => {
        try {
          const productSnap = await getDoc(doc(db, resolveStockProductDocPath(companyId, productId)));
          return productSnap.exists()
            ? { id: productSnap.id, ...productSnap.data() }
            : null;
        } catch (error) {
          console.error('Error cargando producto vinculado para escaneo:', error);
          return null;
        }
      })
    ).then((products) => {
      if (!isCancelled) {
        setLinkedStockProducts(products.filter(Boolean));
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [companyId, destination?.id, destination?.operationalLoads, destination?.incomingOperationalLoads]);

  const validateScanText = async (value, source = 'manual_or_scanner') => {
    const result = resolveScanValue(value, {
      destination,
      route: route || { id: routeId },
      products: linkedStockProducts,
      operationalLoads: destination?.operationalLoads || [],
      incomingOperationalLoads: destination?.incomingOperationalLoads || [],
    });

    setScanResult(result);

    if (result.status === 'empty') {
      showMessage('Escribí o escaneá un código para validar.', 'warning');
      return;
    }

    setIsSavingScan(true);

    try {
      await addDoc(collection(db, resolveScanEventsCollectionPath(companyId)), {
        scannedValue: result.scannedValue,
        rawValue: value,
        resultStatus: result.status,
        targetType: result.targetType,
        targetId:
          result.match?.entity?.id ||
          result.match?.entity?.loadId ||
          result.match?.entity?.stockProductId ||
          '',
        targetLabel: result.match?.label || '',
        matchCount: result.matches.length,
        contextType: 'logistics_destination',
        companyId,
        routeId,
        destinationId: destination?.id || '',
        destinationName: destination?.name || '',
        destinationAddress: destination?.address || '',
        source,
        createdByRole: userRole || 'operador',
        ...actorSnapshot,
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error guardando evento de escaneo:', error);
      showMessage('Se validó el código, pero no pude guardar el evento.', 'warning');
      setIsSavingScan(false);
      return;
    }

    setIsSavingScan(false);

    if (result.status === 'found') {
      showMessage(`${getScanTargetLabel(result.targetType)} validado: ${result.match.label}`, 'success');
    } else if (result.status === 'multiple') {
      showMessage('El código tiene varias coincidencias. Revisá antes de confirmar la operación.', 'warning');
    } else {
      showMessage('Código registrado sin coincidencia operativa.', 'warning');
    }
  };

  const handleValidateScan = async (event) => {
    event?.preventDefault?.();
    await validateScanText(scanValue, 'manual_or_scanner');
  };

  const startCameraScanner = async () => {
    setCameraError('');

    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      setCameraError('Este dispositivo no permite abrir la cámara desde la app.');
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('El navegador no permite usar la cámara. Probá con carga manual o pistola.');
      return;
    }

    if (!('BarcodeDetector' in window)) {
      setCameraError('Este navegador todavía no permite leer QR/código de barras directo. Probá con pistola o escribí el código.');
      return;
    }

    setCameraScannerOpen(true);
    setIsCameraStarting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      cameraStreamRef.current = stream;

      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play();
      }

      const detector = new window.BarcodeDetector({
        formats: [
          'qr_code',
          'ean_13',
          'ean_8',
          'code_128',
          'code_39',
          'upc_a',
          'upc_e',
          'itf',
          'data_matrix'
        ]
      });

      setIsCameraStarting(false);

      cameraScanTimerRef.current = window.setInterval(async () => {
        const video = cameraVideoRef.current;
        if (!video || video.readyState < 2) return;

        try {
          const codes = await detector.detect(video);
          const rawValue = codes?.[0]?.rawValue;
          if (!rawValue) return;

          setScanValue(rawValue);
          stopCameraScanner();
          await validateScanText(rawValue, 'camera_barcode_detector');
        } catch (scanError) {
          console.error('Error leyendo código desde cámara:', scanError);
        }
      }, 500);
    } catch (error) {
      console.error('Error abriendo cámara para escaneo:', error);
      stopCameraScanner();
      setCameraError('No pude abrir la cámara. Revisá permisos del navegador o usá carga manual.');
    }
  };

  if (!destination) return null;

  const mapsQuery = encodeURIComponent(
    `${destination.address || ''} ${destination.apartment || ''}, ${destination.locality || ''}, ${
      destination.partido ? `${destination.partido}, ` : ''
    }${destination.province || ''}, ${destination.postalCode || ''}, Argentina`
  );

  const isTransporterView = userRole === 'transportista';

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    setEvidenceUploadState(buildEvidenceUploadState({
      active: true,
      phase: 'compressing',
      label: 'Preparando evidencia',
      detail: 'Comprimiendo la foto para que pese menos.',
      progress: 8,
    }));

    try {
      const uploadedAt = new Date();
      const compressedFile = await withTimeout(
        compressEvidenceImage(file),
        EVIDENCE_COMPRESSION_TIMEOUT_MS,
        'La compresión tardó demasiado. Probá con una foto más liviana.'
      );
      const storage = getStorage();
      const safeDestinationId = sanitizeStorageFileName(destination.id || 'destino');
      const safeRouteId = sanitizeStorageFileName(routeId || route?.id || 'ruta');
      const safeFileName = sanitizeStorageFileName(compressedFile.name || file.name || 'evidencia.jpg');
      const storagePath = `remitos/${appId}/${safeDestinationId}/${Date.now()}-${safeRouteId}-${safeFileName}`;
      const storageRef = ref(storage, storagePath);

      setEvidenceUploadState(buildEvidenceUploadState({
        active: true,
        phase: 'uploading',
        label: 'Subiendo evidencia',
        detail: 'Guardando la imagen comprimida en Storage.',
        progress: 18,
      }));

      const snapshot = await uploadEvidenceBlob({
        storageRef,
        blob: compressedFile.blob,
        metadata: {
          contentType: compressedFile.contentType,
          customMetadata: {
            companyId: String(companyId || 'default'),
            routeId: String(routeId || route?.id || ''),
            destinationId: String(destination?.id || ''),
            uploadedByRole: String(userRole || 'operador'),
          },
        },
        onProgress: (uploadProgress) => {
          setEvidenceUploadState(buildEvidenceUploadState({
            active: true,
            phase: 'uploading',
            label: 'Subiendo evidencia',
            detail: `${uploadProgress}% de la foto ya está en Storage.`,
            progress: Math.min(86, 18 + Math.round(uploadProgress * 0.68)),
          }));
        },
      });

      setEvidenceUploadState(buildEvidenceUploadState({
        active: true,
        phase: 'saving',
        label: 'Cerrando registro',
        detail: 'Vinculando la evidencia con este destino.',
        progress: 90,
      }));

      const photoURL = await withTimeout(
        getDownloadURL(snapshot.ref),
        EVIDENCE_SAVE_TIMEOUT_MS,
        'La foto subió, pero tardó demasiado en devolver la URL.'
      );
      const evidenceItem = {
        id: `ev-${uploadedAt.getTime()}`,
        type: 'photo',
        label: 'Foto de evidencia',
        url: photoURL,
        storagePath,
        fileName: safeFileName,
        originalFileName: file.name || safeFileName,
        contentType: compressedFile.contentType,
        originalSize: compressedFile.originalSize,
        compressedSize: compressedFile.compressedSize,
        compressionApplied: compressedFile.compressionApplied,
        width: compressedFile.width,
        height: compressedFile.height,
        uploadedAt: uploadedAt.toISOString(),
        uploadedByRole: userRole || 'operador',
        companyId,
        routeId: routeId || route?.id || '',
        destinationId: destination?.id || '',
      };
      const updatedEvidenceFiles = [evidenceItem, ...(destination.evidenceFiles || [])].slice(0, 20);
      const updatedDestination = {
        ...destination,
        photoURL,
        evidenceFiles: updatedEvidenceFiles,
        latestEvidence: evidenceItem,
      };

      await addDoc(collection(db, resolveScanEventsCollectionPath(companyId)), {
        scannedValue: '',
        rawValue: '',
        resultStatus: 'evidence_uploaded',
        targetType: 'destination',
        targetId: destination?.id || '',
        targetLabel: destination?.name || '',
        matchCount: 1,
        contextType: 'logistics_destination_evidence',
        evidenceType: 'photo',
        evidenceUrl: photoURL,
        evidenceStoragePath: storagePath,
        evidenceFileName: safeFileName,
        evidenceOriginalSize: compressedFile.originalSize,
        evidenceCompressedSize: compressedFile.compressedSize,
        companyId,
        routeId: routeId || route?.id || '',
        destinationId: destination?.id || '',
        destinationName: destination?.name || '',
        destinationAddress: destination?.address || '',
        source: 'photo_upload',
        createdByRole: userRole || 'operador',
        ...actorSnapshot,
        createdAt: serverTimestamp(),
      }).catch((error) => {
        console.error('Error guardando evento de evidencia:', error);
      });

      await withTimeout(
        Promise.resolve(onUpdate(updatedDestination)),
        EVIDENCE_SAVE_TIMEOUT_MS,
        'La foto subió, pero el destino tardó demasiado en guardar el registro.'
      );
      setEvidenceUploadState(buildEvidenceUploadState({
        active: true,
        phase: 'done',
        label: 'Evidencia guardada',
        detail: compressedFile.compressionApplied
          ? 'Foto comprimida, subida y vinculada al destino.'
          : 'Archivo subido y vinculado al destino.',
        progress: 100,
      }));
      showMessage(
        compressedFile.compressionApplied
          ? 'Evidencia subida y comprimida con éxito.'
          : 'Evidencia subida con éxito.',
        'success'
      );
      setEvidenceSuccessMessage('Foto subida con éxito. Ya quedó vinculada a este destino y lista para revisarla en administración.');
      window.setTimeout(() => {
        setEvidenceSuccessMessage('');
      }, 8000);
      window.setTimeout(() => {
        setEvidenceUploadState(buildEvidenceUploadState());
      }, 1800);
    } catch (error) {
      console.error('Error al subir la foto:', error);
      const friendlyMessage = getEvidenceUploadErrorMessage(error);
      setEvidenceUploadState(buildEvidenceUploadState());
      showMessage(friendlyMessage, 'error');
    } finally {
      setIsUploading(false);
      if (event.target) event.target.value = '';
    }
  };

  const handleAddAdminNote = () => {
    if (!adminNote.trim()) return;

    const newNote = {
      text: adminNote,
      timestamp: new Date(),
      read: false
    };

    const updatedNotes = [...(destination.adminNotes || []), newNote];
    onUpdate({ ...destination, adminNotes: updatedNotes });
    setAdminNote('');
    showMessage('Nota enviada al transportista', 'success');
  };

  const handleStatusUpdate = async (status) => {
    if (isSubmittingStatus) return;
    setIsSubmittingStatus(true);

    const nextDestinationPayload = { ...destination, orderDetail, status };
    const useOptimisticTransporterClose =
      isTransporterView && ['completed', 'incomplete', 'cancelled'].includes(String(status || '').trim().toLowerCase());

    if (useOptimisticTransporterClose) {
      if (typeof showMessage === 'function') {
        showMessage('Guardando cambio de estado...', 'info', 2500);
      }
      onClose();
      Promise.resolve(onUpdate(nextDestinationPayload)).catch((error) => {
        console.error('Error actualizando estado del destino:', error);
        if (typeof showMessage === 'function') {
          showMessage('No pude guardar el cambio de estado. Probá nuevamente.', 'error');
        }
      });
      return;
    }

    try {
      await Promise.resolve(onUpdate(nextDestinationPayload));
      onClose();
    } catch (error) {
      console.error('Error actualizando estado del destino:', error);
      if (typeof showMessage === 'function') {
        showMessage('No pude guardar el cambio de estado. Probá nuevamente.', 'error');
      }
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const handleSaveOrderDetail = () => {
    onUpdate({ ...destination, orderDetail }, false);
    showMessage('Detalle del pedido guardado.', 'success');
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[1000] bg-slate-950/55 backdrop-blur-md"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div className="flex h-full w-full items-stretch justify-center sm:items-center sm:p-4 lg:p-6">
        <div
          className="flex h-[100dvh] w-full min-h-0 flex-col overflow-hidden bg-white shadow-2xl sm:h-auto sm:max-h-[94vh] sm:max-w-5xl sm:rounded-[30px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative shrink-0 overflow-hidden bg-slate-900 text-white">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(34,211,238,0.14),transparent_38%),radial-gradient(circle_at_top_left,rgba(255,255,255,0.08),transparent_28%)]" />
            <div className="relative px-4 pb-4 pt-4 sm:px-6 sm:pb-5 sm:pt-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 pr-1">
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <h3 className="truncate text-xl font-bold leading-tight text-white sm:text-2xl md:text-[1.8rem]">
                      {destination.name}
                    </h3>
                    <span className="hidden h-6 w-px bg-white/15 sm:block" />
                    <p className="truncate text-sm font-medium text-slate-100 sm:text-base">
                      {destination.address} {destination.apartment && `(${destination.apartment})`}
                    </p>
                  </div>

                  {locationLine && (
                    <p className="mt-2 text-xs text-slate-300 sm:text-sm">{locationLine}</p>
                  )}

                  {!isHeaderCollapsed && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${statusMeta.badge}`}>
                        {statusMeta.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2 pl-1">
                  <a
                    href={`https://maps.google.com/?q=${mapsQuery}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/15"
                  >
                    <Icon
                      path="M9 6.75V15m6-6v8.25m.5-10.5h-13a.5.5 0 00-.5.5v13a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-13a.5.5 0 00-.5-.5z"
                      className="h-4 w-4"
                    />
                    <span className="hidden sm:inline">Navegar</span>
                  </a>

                  <button
                    type="button"
                    onClick={toggleHeaderCollapsed}
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/15 ${showHeaderPulse && isHeaderCollapsed ? 'animate-pulse' : ''}`}
                    aria-label={isHeaderCollapsed ? 'Expandir cabecera' : 'Plegar cabecera'}
                  >
                    <Icon
                      path={isHeaderCollapsed ? 'M19.5 14.25L12 6.75l-7.5 7.5' : 'M4.5 9.75L12 17.25l7.5-7.5'}
                      className="h-5 w-5"
                    />
                  </button>

                  <button
                    onClick={onClose}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/15"
                    aria-label="Cerrar"
                  >
                    <Icon path="M6 18L18 6M6 6l12 12" className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {!isHeaderCollapsed && metaItems.length > 0 && (
                <div className="mt-4">
                  <div
                    className="grid gap-2"
                    style={{ gridTemplateColumns: `repeat(${metaItems.length}, minmax(0, 1fr))` }}
                  >
                    {metaItems.map((item, index) => (
                      <div
                        key={`${item.label}-${index}`}
                        className="flex min-w-0 items-center justify-center gap-1.5 rounded-2xl border border-white/10 bg-white/8 px-2 py-2 text-center"
                      >
                        <Icon path={item.icon} className="h-3.5 w-3.5 shrink-0 text-slate-200" />
                        <span className="truncate text-[11px] font-semibold text-slate-100 sm:text-xs">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!isHeaderCollapsed && (destination?.contactName || destination?.contactPhone) && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {destination?.contactName ? (
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white">
                      Contacto: {destination.contactName}
                    </span>
                  ) : null}
                  {destination?.contactPhone ? (
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white">
                      Tel: {destination.contactPhone}
                    </span>
                  ) : null}
                </div>
              )}

              {isHeaderCollapsed && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${statusMeta.badge}`}>
                    {statusMeta.label}
                  </span>
                  {destination?.contactName ? (
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white">
                      {destination.contactName}
                    </span>
                  ) : null}
                  {destination?.contactPhone ? (
                    <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-bold text-white">
                      {destination.contactPhone}
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50/90 px-4 py-4 sm:px-7 sm:py-6">
            <div className="space-y-4">
              <div className="rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-slate-900 text-white">
                    <Icon path="M6.75 3v2.25M17.25 3v2.25M3 18.75V8.25a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 8.25v10.5m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-900 sm:text-lg">Detalle operativo</h4>
                    <p className="text-sm text-slate-500">Lectura clara de la parada, contacto y tarea operativa.</p>
                  </div>
                </div>

                {destination.description && (
                  <div className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    <span className="font-semibold text-slate-900">Pedido:</span> {destination.description}
                  </div>
                )}

                {destination.deliversTo && (
                  <div className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    <span className="font-semibold text-slate-900">Destino final:</span>{' '}
                    {destination.deliversTo === 'oficina' ? 'Oficina' : destination.deliversTo}
                  </div>
                )}

                {(destination.operationalLoads || []).length > 0 && (
                  <div className="mb-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h5 className="text-sm font-semibold text-slate-900">Carga operativa</h5>
                        <p className="text-xs text-slate-500">Planilla operativa del destino</p>
                      </div>
                      <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                        {(destination.operationalLoads || []).length} asociada{(destination.operationalLoads || []).length > 1 ? 's' : ''}
                      </span>
                    </div>

                    <div className="overflow-hidden rounded-[24px] border border-slate-300/90 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                      <div className="grid grid-cols-[minmax(0,1.9fr)_120px_72px] gap-4 border-b border-slate-300/80 bg-[linear-gradient(180deg,rgba(15,23,42,0.92),rgba(30,41,59,0.92))] px-4 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                        <span>Producto</span>
                        <span>Acción</span>
                        <span>Obs.</span>
                      </div>

                      {(destination.operationalLoads || []).map((load, index) => {
                        const linkedProduct = linkedStockProductsById.get(String(load.stockProductId || load.productId || '')) || null;
                        const scanChips = buildLoadScanChips(load, linkedProduct);
                        const operationalLoadLabel = resolveOperationalLoadLabel(load, linkedProduct);

                        return (
                        <div
                          key={load.id}
                          className="border-b border-slate-200/90 px-4 py-4 last:border-b-0"
                          style={{
                            background:
                              index % 2 === 0
                                ? 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.97) 100%)'
                                : 'linear-gradient(180deg, rgba(248,250,252,0.98) 0%, rgba(241,245,249,0.95) 100%)',
                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.82), inset 0 -1px 0 rgba(226,232,240,0.6)'
                          }}
                        >
                          <div className="grid grid-cols-[minmax(0,1.9fr)_120px_72px] gap-4">
                            <div className="min-w-0">
                              <div className="mt-1 text-[15px] font-semibold text-slate-900">
                                {load.quantity} {operationalLoadLabel}
                              </div>
                              {scanChips.length > 0 ? (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {scanChips.map((chip) => (
                                    <span
                                      key={`${load.id}-${chip.label}-${chip.value}`}
                                      className={`rounded-full border px-2 py-1 text-[11px] font-bold ${getScanChipToneClass(chip.tone)}`}
                                    >
                                      {chip.label} {chip.value}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                            </div>

                            <div className="min-w-0">
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                <div className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[13px] font-semibold text-slate-700 shadow-sm">
                                  {getDestinationOperationRoleLabel(load.operationType)}
                                </div>
                                {load.stockOperationReason ? (
                                  <div className="inline-flex rounded-full border border-fuchsia-200 bg-fuchsia-50 px-2.5 py-1 text-[13px] font-semibold text-fuchsia-700 shadow-sm">
                                    {formatStockReasonLabel(load.stockOperationReason)}
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div className="min-w-0">
                              <div className="mt-1">
                                {load.description ? (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedObservation(load.description)}
                                    className="rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                                  >
                                    Ver
                                  </button>
                                ) : (
                                  <span className="text-xs text-slate-400">-</span>
                                )}
                              </div>
                            </div>
                          </div>

                          {(load.requiresEvidenceOnPickup || load.requiresEvidenceOnDelivery) && (
                            <div className="mt-3 rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.96),rgba(241,245,249,0.96))] px-3 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.75)]">
                              <div className="flex flex-wrap gap-2">
                                {load.requiresEvidenceOnPickup && (
                                  <button
                                    type="button"
                                    onClick={openActionHub}
                                    className="rounded-xl border border-amber-200 bg-[linear-gradient(180deg,#fff7ed,#fef3c7)] px-3 py-1.5 text-xs font-semibold text-amber-700 shadow-sm transition hover:bg-amber-100"
                                  >
                                    Evidencia retiro
                                  </button>
                                )}
                                {load.requiresEvidenceOnDelivery && (
                                  <button
                                    type="button"
                                    onClick={openActionHub}
                                    className="rounded-xl border border-cyan-200 bg-[linear-gradient(180deg,#ecfeff,#cffafe)] px-3 py-1.5 text-xs font-semibold text-cyan-700 shadow-sm transition hover:bg-cyan-100"
                                  >
                                    Evidencia entrega
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(destination.incomingOperationalLoads || []).length > 0 && (
                  <div className="mb-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h5 className="text-sm font-semibold text-cyan-900">Recibe carga vinculada</h5>
                        <p className="text-xs text-cyan-700">
                          Cargas retiradas en otro punto de la ruta
                        </p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {(destination.incomingOperationalLoads || []).map((incoming) => {
                        const linkedProduct = linkedStockProductsById.get(String(incoming.stockProductId || incoming.productId || '')) || null;
                        const scanChips = buildLoadScanChips(incoming, linkedProduct);
                        const incomingLabel =
                          String(
                            incoming.title ||
                              incoming.productNameSnapshot ||
                              incoming.stockProductNameSnapshot ||
                              incoming.productName ||
                              ''
                          ).trim() || 'Producto';

                        return (
                        <div
                          key={`${incoming.sourceDestinationId}-${incoming.loadId}`}
                          className="rounded-xl border border-cyan-100 bg-white px-3 py-3 shadow-sm"
                        >
                          <p className="text-sm font-semibold text-slate-900">
                            {incomingLabel}
                          </p>
                          <p className="mt-1 text-sm font-medium text-cyan-700">
                            Recibe desde {incoming.sourceDestinationName || 'otro punto'} · llega a este destino
                          </p>
                          {scanChips.length > 0 ? (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {scanChips.map((chip) => (
                                <span
                                  key={`${incoming.loadId || incoming.id}-${chip.label}-${chip.value}`}
                                  className={`rounded-full border px-2 py-1 text-[11px] font-bold ${getScanChipToneClass(chip.tone)}`}
                                >
                                  {chip.label} {chip.value}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <textarea
                  value={orderDetail}
                  onChange={(e) => setOrderDetail(e.target.value)}
                  placeholder="Añadir detalle del pedido..."
                  className="h-32 w-full rounded-2xl border border-slate-200 bg-white p-3 text-sm text-slate-800 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                />
              </div>

              <div className="rounded-2xl border border-amber-200 bg-amber-50/80 p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-500 text-white">
                    <Icon path="M7.5 8.25h9m-9 3h6m-7.5 8.25h9A2.25 2.25 0 0018.75 17V7A2.25 2.25 0 0016.5 4.75h-9A2.25 2.25 0 005.25 7v10A2.25 2.25 0 007.5 19.25z" className="h-4 w-4" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Notas operativas</h4>
                    <p className="text-xs text-slate-600">Mensajes útiles entre administración y chofer.</p>
                  </div>
                </div>

                <div className="mb-3 space-y-2 pr-1">
                  {(destination.adminNotes || []).length === 0 && (
                    <div className="rounded-xl bg-white/80 px-3 py-3 text-sm text-slate-500">
                      No hay notas todavía.
                    </div>
                  )}

                  {(destination.adminNotes || []).map((note, i) => (
                    <div key={i} className="rounded-xl bg-white px-3 py-2.5 shadow-sm ring-1 ring-amber-100">
                      <p className="text-sm text-slate-700">{note.text}</p>
                      <p className="mt-1 text-[11px] font-medium text-slate-400">{formatNoteTime(note.timestamp)}</p>
                    </div>
                  ))}
                </div>

                {userRole === 'admin' && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      placeholder="Escribir nota..."
                      className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    />
                    <button
                      onClick={handleAddAdminNote}
                      className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white transition hover:bg-amber-600"
                      aria-label="Enviar nota"
                    >
                      <Icon
                        path="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
                        className="h-5 w-5"
                      />
                    </button>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-600 text-white">
                    <Icon path="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A3.375 3.375 0 0011.25 11.625v2.625m8.25 0v1.5A2.25 2.25 0 0117.25 18h-10.5A2.25 2.25 0 014.5 15.75v-1.5m15 0h-15" className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-slate-900">Evidencia operativa</h4>
                    <p className="text-xs text-slate-500">Fotos comprimidas, remitos o comprobantes de esta parada.</p>
                  </div>
                  {destination.photoURL ? (
                    <a
                      href={destination.photoURL}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-700 shadow-sm transition hover:border-cyan-200 hover:text-cyan-700"
                    >
                      <Icon path="M12 16.5v-9m0 9l-3.75-3.75M12 16.5l3.75-3.75M3.75 19.5h16.5" className="h-4 w-4" />
                      Descargar
                    </a>
                  ) : null}
                </div>

                {evidenceSuccessMessage ? (
                  <div className="mb-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-800 shadow-sm">
                    {evidenceSuccessMessage}
                  </div>
                ) : null}

                {destination.photoURL ? (
                  <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
                    <img src={destination.photoURL} alt="Comprobante" className="max-h-[360px] w-full object-contain bg-slate-100" />
                    {destination.latestEvidence ? (
                      <div className="border-t border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-500">
                        <span className="text-slate-800">Última evidencia</span>
                        {' · '}
                        {destination.latestEvidence.uploadedByRole || 'operador'}
                        {destination.latestEvidence.compressedSize ? ` · ${(destination.latestEvidence.compressedSize / 1024).toFixed(0)} KB` : ''}
                        {destination.latestEvidence.compressionApplied ? ' · comprimida' : ''}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                    Todavía no hay evidencia cargada.
                  </div>
                )}

                {(destination.evidenceFiles || []).length > 1 ? (
                  <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                      Historial de evidencia
                    </p>
                    <div className="mt-2 space-y-2">
                      {(destination.evidenceFiles || []).slice(0, 4).map((item, index) => (
                        <a
                          key={item.id || item.url || index}
                          href={item.url}
                          download={item.originalFileName || item.fileName || `evidencia-${index + 1}.jpg`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:border-cyan-200 hover:text-cyan-700"
                        >
                          <span className="truncate">{item.label || item.originalFileName || `Evidencia ${index + 1}`}</span>
                          <span className="shrink-0 text-slate-400">
                            {item.compressedSize ? `${(item.compressedSize / 1024).toFixed(0)} KB` : 'ver'}
                          </span>
                        </a>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-slate-200 bg-white/95 px-4 py-4 backdrop-blur sm:px-6">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept="image/*"
              capture="environment"
            />

            <button
              type="button"
              onClick={() => {
                if (actionHubOpen) {
                  setActionHubOpen(false);
                  return;
                }
                openActionHub();
              }}
              className="inline-flex min-h-[56px] w-full items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-[linear-gradient(180deg,#111827,#020617)] px-4 py-3 text-sm font-semibold text-white shadow-[0_16px_36px_rgba(15,23,42,0.18)] transition hover:brightness-110"
            >
              <Icon
                path="M3.75 4.875c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-3.75A1.125 1.125 0 013.75 8.625v-3.75zm10.5 0c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-3.75a1.125 1.125 0 01-1.125-1.125v-3.75zm-10.5 10.5c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-3.75a1.125 1.125 0 01-1.125-1.125v-3.75zm10.5 1.125h6m-6 3.75h6m-3-6v6"
                className="h-5 w-5"
              />
              Escaneo / evidencia
            </button>

            {actionHubOpen && (
              <div ref={actionHubRef} className="mt-3 rounded-[24px] border border-slate-200 bg-slate-50 p-3 shadow-inner">
                <form onSubmit={handleValidateScan} className="rounded-2xl border border-cyan-200 bg-white p-3 shadow-sm">
                  <div className="flex items-start gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-cyan-600 text-white">
                      <Icon path="M3.75 4.875h3.75m-3.75 3.75h3.75m-3.75 6.75h3.75m-3.75 3.75h3.75m4.5-14.25h8.25m-8.25 3.75h8.25m-8.25 6.75h8.25m-8.25 3.75h8.25" className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900">Validar código</p>
                      <p className="text-xs text-slate-500">Pistola, QR o carga manual: todo entra como texto.</p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                    <input
                      ref={scanInputRef}
                      type="text"
                      value={scanValue}
                      onChange={(event) => setScanValue(event.target.value)}
                      placeholder="Escanear o escribir código"
                      className="min-h-[48px] rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-800 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100"
                    />
                    <button
                      type="submit"
                      disabled={isSavingScan}
                      className="min-h-[48px] rounded-2xl bg-cyan-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-cyan-700 disabled:opacity-60"
                    >
                      {isSavingScan ? 'Validando...' : 'Validar'}
                    </button>
                  </div>

                  {scanResult && scanResult.status !== 'empty' && (
                    <div className={`mt-3 rounded-2xl border px-3 py-3 text-sm ${getScanResultTone(scanResult.status)}`}>
                      <p className="font-bold">
                        {scanResult.status === 'found'
                          ? `${getScanTargetLabel(scanResult.targetType)} encontrado`
                          : scanResult.status === 'multiple'
                            ? 'Varias coincidencias encontradas'
                            : 'Sin coincidencia operativa'}
                      </p>
                      {scanResult.status === 'not_found' ? (
                        <p className="mt-1 text-xs font-semibold">
                          El código se leyó y quedó registrado, pero no coincide con esta parada, carga o producto cargado en el sistema.
                        </p>
                      ) : null}
                      {scanResult.match ? (
                        <p className="mt-1 font-medium">{scanResult.match.label}</p>
                      ) : null}
                      {scanResult.status === 'multiple' ? (
                        <div className="mt-2 space-y-1">
                          {scanResult.matches.slice(0, 4).map((match) => (
                            <p key={`${match.targetType}-${match.entity?.id || match.label}`} className="text-xs font-semibold">
                              {getScanTargetLabel(match.targetType)} · {match.label}
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )}
                </form>

                <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className="flex items-start gap-2">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white">
                      <Icon path="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.174C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.17a47.865 47.865 0 00-1.134-.174 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.25 2.25 0 00-1.907-1.059H9.555a2.25 2.25 0 00-1.907 1.059l-.821 1.316zM16.5 13.5a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-slate-900">Cámara del celular</p>
                      <p className="text-xs text-slate-500">Lee QR y códigos de barra si el navegador lo permite.</p>
                    </div>
                  </div>

                  {cameraScannerOpen ? (
                    <div className="mt-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-3 py-3 text-sm font-semibold text-cyan-800">
                      Cámara abierta en visor completo. La lectura es automática.
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={startCameraScanner}
                      className="mt-3 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-cyan-200 bg-[linear-gradient(180deg,#ecfeff,#cffafe)] px-4 py-3 text-sm font-bold text-cyan-800 shadow-sm transition hover:brightness-105"
                    >
                      <Icon path="M3.75 4.875h3.75m-3.75 3.75h3.75m-3.75 6.75h3.75m-3.75 3.75h3.75m4.5-14.25h8.25m-8.25 3.75h8.25m-8.25 6.75h8.25m-8.25 3.75h8.25" className="h-5 w-5" />
                      Abrir cámara para escanear
                    </button>
                  )}

                  {cameraError ? (
                    <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800">
                      {cameraError}
                    </div>
                  ) : null}
                </div>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="mt-3 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
                >
                  {isUploading ? (
                    <Icon
                      path="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992"
                      className="h-5 w-5"
                      spin={true}
                    />
                  ) : (
                    <Icon
                      path="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                      className="h-5 w-5"
                    />
                  )}
                  {isUploading ? 'Comprimiendo y subiendo...' : destination.photoURL ? 'Sumar nueva evidencia' : 'Adjuntar evidencia'}
                </button>
              </div>
            )}

            <div className={`mt-4 grid gap-3 ${isTransporterView ? 'grid-cols-2' : 'grid-cols-2'}`}>
              <button
                onClick={handleSaveOrderDetail}
                disabled={isSubmittingStatus}
                className={`flex flex-col items-center justify-center gap-2 rounded-[22px] border border-slate-200 bg-slate-100 px-4 text-center font-semibold text-slate-800 shadow-sm transition hover:bg-slate-200 ${isTransporterView ? 'min-h-[88px] py-3.5 text-[13px]' : 'min-h-[104px] py-4.5 text-sm'}`}
              >
                <Icon
                  path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  className="h-6 w-6"
                />
                <span className="leading-tight">Guardar detalle</span>
              </button>

              <button
                onClick={() => handleStatusUpdate('incomplete')}
                disabled={isSubmittingStatus}
                className={`flex flex-col items-center justify-center gap-2 rounded-[22px] bg-amber-500 px-4 text-center font-semibold text-white shadow-sm transition hover:bg-amber-600 ${isTransporterView ? 'min-h-[88px] py-3.5 text-[13px]' : 'min-h-[104px] py-4.5 text-sm'}`}
              >
                <Icon
                  path="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                  className="h-6 w-6"
                />
                <span className="leading-tight">Marcar incompleto</span>
              </button>

              <button
                onClick={() => handleStatusUpdate('completed')}
                disabled={isSubmittingStatus}
                className={`flex flex-col items-center justify-center gap-2 rounded-[22px] bg-emerald-600 px-4 text-center font-semibold text-white shadow-sm transition hover:bg-emerald-700 ${isTransporterView ? 'min-h-[88px] py-3.5 text-[13px]' : 'min-h-[104px] py-4.5 text-sm'}`}
              >
                <Icon
                  path="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  className="h-6 w-6"
                />
                <span className="leading-tight">{isSubmittingStatus ? 'Guardando...' : completionActionLabel}</span>
              </button>

              <button
                onClick={() => handleStatusUpdate('cancelled')}
                disabled={isSubmittingStatus}
                className={`flex flex-col items-center justify-center gap-2 rounded-[22px] bg-rose-600 px-4 text-center font-semibold text-white shadow-sm transition hover:bg-rose-700 ${isTransporterView ? 'min-h-[88px] py-3.5 text-[13px]' : 'min-h-[104px] py-4.5 text-sm'}`}
              >
                <Icon
                  path="M6 18L18 6M6 6l12 12"
                  className="h-6 w-6"
                />
                <span className="leading-tight">{cancellationActionLabel}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {evidenceUploadState.active && (
        <div
          className="fixed inset-0 z-[1250] flex items-center justify-center bg-slate-950/72 px-5 backdrop-blur-md"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="w-full max-w-sm overflow-hidden rounded-[30px] border border-white/12 bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(2,6,23,0.98))] p-5 text-white shadow-[0_30px_90px_rgba(2,6,23,0.42)]">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-cyan-400/15 text-cyan-200 ring-1 ring-cyan-300/25">
                <Icon
                  path={
                    evidenceUploadState.phase === 'done'
                      ? 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                      : 'M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0011.667 0l3.181-3.183m-4.991-2.691v4.992h4.992'
                  }
                  className="h-6 w-6"
                  spin={evidenceUploadState.phase !== 'done'}
                />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-black uppercase tracking-[0.24em] text-cyan-200">
                  Evidencia
                </p>
                <h4 className="mt-1 text-xl font-black leading-tight text-white">
                  {evidenceUploadState.label || 'Procesando archivo'}
                </h4>
                <p className="mt-2 text-sm font-medium leading-relaxed text-slate-300">
                  {evidenceUploadState.detail || 'Estamos preparando la evidencia operativa.'}
                </p>
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                <span>
                  {evidenceUploadState.phase === 'compressing'
                    ? 'Compresión'
                    : evidenceUploadState.phase === 'uploading'
                      ? 'Subida'
                      : evidenceUploadState.phase === 'saving'
                        ? 'Registro'
                        : 'Listo'}
                </span>
                <span>{Math.round(evidenceUploadState.progress || 0)}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-white/10 ring-1 ring-white/10">
                <div
                  className="h-full rounded-full bg-[linear-gradient(90deg,#22d3ee,#34d399)] shadow-[0_0_28px_rgba(34,211,238,0.45)] transition-all duration-300"
                  style={{ width: `${Math.min(100, Math.max(4, Number(evidenceUploadState.progress) || 0))}%` }}
                />
              </div>
            </div>

            <p className="mt-4 rounded-2xl border border-white/10 bg-white/8 px-3 py-2 text-xs font-semibold leading-relaxed text-slate-300">
              Si Storage no está habilitado o no tiene permisos, G-CENTRA corta la espera y te avisa en pantalla.
            </p>
          </div>
        </div>
      )}

      {selectedObservation && (
        <div className="absolute inset-0 z-[1001] flex items-center justify-center bg-slate-950/45 px-4" onClick={() => setSelectedObservation(null)}>
          <div
            className="w-full max-w-lg rounded-[24px] border border-slate-200 bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-slate-900">Observación del producto</h4>
                <p className="mt-1 text-sm text-slate-500">Detalle asociado a esta carga operativa.</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedObservation(null)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-100"
              >
                <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 rounded-2xl bg-slate-50 px-4 py-4 text-[15px] leading-relaxed text-slate-700">
              {selectedObservation}
            </div>
          </div>
        </div>
      )}

      {cameraScannerOpen && (
        <div
          className="fixed inset-0 z-[1200] flex min-h-[100dvh] flex-col bg-slate-950 text-white"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="relative flex min-h-0 flex-1 flex-col">
            <video
              ref={cameraVideoRef}
              className="absolute inset-0 h-full w-full object-cover"
              muted
              playsInline
            />

            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,transparent_42%,rgba(2,6,23,0.72)_72%)]" />

            <div className="relative z-10 flex items-start justify-between gap-3 px-4 pt-[max(18px,env(safe-area-inset-top))]">
              <div className="max-w-[74%] rounded-3xl bg-slate-950/72 px-4 py-3 backdrop-blur">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-cyan-200">Escaneo automático</p>
                <h3 className="mt-1 text-xl font-black leading-tight text-white">Apuntá al código</h3>
                <p className="mt-1 text-xs font-medium text-slate-200">
                  No hace falta tocar otro botón: cuando lo detecta, valida solo.
                </p>
              </div>

              <button
                type="button"
                onClick={stopCameraScanner}
                className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/15 text-white backdrop-blur transition hover:bg-white/25"
                aria-label="Cerrar escáner"
              >
                <Icon path="M6 18L18 6M6 6l12 12" className="h-6 w-6" />
              </button>
            </div>

            <div className="pointer-events-none relative z-10 flex min-h-0 flex-1 items-center justify-center px-6">
              <div className="relative h-[210px] w-full max-w-[340px] rounded-[34px] border-2 border-cyan-300 shadow-[0_0_0_999px_rgba(2,6,23,0.38),0_0_42px_rgba(34,211,238,0.34)]">
                <div className="absolute -left-1 -top-1 h-10 w-10 rounded-tl-[34px] border-l-4 border-t-4 border-white" />
                <div className="absolute -right-1 -top-1 h-10 w-10 rounded-tr-[34px] border-r-4 border-t-4 border-white" />
                <div className="absolute -bottom-1 -left-1 h-10 w-10 rounded-bl-[34px] border-b-4 border-l-4 border-white" />
                <div className="absolute -bottom-1 -right-1 h-10 w-10 rounded-br-[34px] border-b-4 border-r-4 border-white" />
                <div className="absolute left-5 right-5 top-1/2 h-px bg-cyan-200/90 shadow-[0_0_18px_rgba(103,232,249,0.95)]" />
              </div>
            </div>

            <div className="relative z-10 px-4 pb-[max(18px,env(safe-area-inset-bottom))]">
              <div className="rounded-[26px] border border-white/12 bg-slate-950/78 p-4 text-center backdrop-blur">
                <p className="text-sm font-bold text-white">
                  {isCameraStarting ? 'Abriendo cámara...' : 'Buscando QR o código de barras'}
                </p>
                <p className="mt-1 text-xs font-medium text-slate-300">
                  Si no lo toma, acercá el código al marco o usá la carga manual.
                </p>
                <button
                  type="button"
                  onClick={stopCameraScanner}
                  className="mt-3 min-h-[48px] w-full rounded-2xl border border-white/15 bg-white/10 px-4 text-sm font-bold text-white transition hover:bg-white/20"
                >
                  Cerrar y volver al destino
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : modalContent;
};

export default DestinationDetailModal;
